/**
 * AppShell.js — sidebar, topbar and content area.
 *
 * DEFECTS IN v1 THAT THIS FIXES
 *  - The header logged the entire Firebase user object, including the phone
 *    number and decoded custom claims, to the browser console on every render.
 *  - `const logout = () => { }` — the only sign-out button did nothing, and
 *    `signOut` was imported nowhere in the source.
 *  - The user menu opened on CSS hover only, so it was unreachable by
 *    keyboard; its trigger was `<a href="#">` and its images had `alt='...'`
 *    (a literal ellipsis) instead of alt text.
 *  - There was no mobile layout: the shell was pinned to desktop widths.
 *
 * The topbar's theme toggle used to hold its own copy of the theme in React
 * state, so a theme saved in Settings > Appearance was not reflected here until
 * the next full page load. Both now read the shared hook.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  ActivityIcon,
  BankIcon,
  BookIcon,
  BalanceIcon,
  CashFlowIcon,
  ChartOfAccountsIcon,
  ChevronIcon,
  DarkThemeIcon,
  DashboardIcon,
  DataIcon,
  ItemIcon,
  LightThemeIcon,
  ManufacturingIcon,
  MenuIcon,
  OfflineIcon,
  OnlineIcon,
  OutstandingIcon,
  PartyIcon,
  PaymentIcon,
  PriceListIcon,
  ReminderIcon,
  RepeatIcon,
  ReportIcon,
  ScanIcon,
  SendIcon,
  SettingsIcon,
  SignOutIcon,
  StockTakeIcon,
  StoreIcon,
  WarehouseIcon,
  voucherTypeIcon,
} from '../ui/icons';
import { useAuth } from '../../context/AuthContext';
import { AccountingProvider } from '../../features/accounting/useAccounting';
import { VOUCHER_GROUPS, voucherConfig } from '../../lib/voucher-types';
import {
  ENTERABLE_TYPES,
  voucherConfig as accountingVoucherConfig,
} from '../../lib/accounting';
import { canOpenRoute } from '../../lib/roles';
import { PageHeadingProvider, usePageHeadingValue } from './page-heading';
import { Button, ConfirmDialog, useToast } from '../ui';
import { initials } from '../../lib/format';
/* One shared theme for the toggle here and the Appearance section in Settings.
   Both used to keep their own copy of it and never saw each other's writes. */
import { useTheme } from '../../hooks/useTheme';
import { useConnection } from '../../hooks/useConnection';

/**
 * The sidebar is GENERATED from the voucher table rather than hand-listed.
 *
 * It used to be a literal array, and when the five document types became ten
 * that array is exactly the sort of thing that ends up missing an entry — v1's
 * sidebar linked to three routes that were never registered at all. Deriving it
 * from `VOUCHER_GROUPS` means a new document type appears in the navigation the
 * moment it exists, and cannot appear before its route does.
 */
const VOUCHER_NAV = VOUCHER_GROUPS.map((group) => ({
  label: group.label,
  links: group.types.map((type) => {
    const config = voucherConfig(type);
    return {
      to: `/vouchers/${config.route}`,
      label: config.plural,
      icon: voucherTypeIcon(type),
    };
  }),
}));

/**
 * The accounting vouchers, generated from their own table for the same reason
 * the billing ones are: a type added without a navigation entry is a type
 * nobody can find, and a navigation entry without a route is v1's sidebar.
 *
 * The labels are the STANDARD accounting names. The specification is explicit
 * that Contra must be called Contra and not "Bank/Cash Transfer", and it is
 * right — an accountant looking for the transfer register looks for Contra, and
 * a shopkeeper who learns the word once has learned the word every other set of
 * books uses.
 *
 * SALES AND PURCHASE ARE DELIBERATELY NOT HERE.
 *
 * They were, briefly, and they were a duplicate: the Billing section already
 * carries Sales and Purchases at the top of the sidebar, so the same two words
 * appeared twice in one column pointing at two different screens, and the
 * reader's job became working out which of the two they wanted. The registers
 * themselves are untouched — `/accounting/vouchers/sales` and
 * `/accounting/vouchers/purchase` still render, read-only as sourced types
 * should, and the Day Book shows every type together. What is gone is the
 * second, ambiguous way in.
 *
 * So this list is exactly the four types a person can ENTER here.
 */
const ACCOUNTING_NAV = ENTERABLE_TYPES.slice(0, 4).map((type) => {
  const config = accountingVoucherConfig(type);
  return {
    to: `/accounting/vouchers/${config.route}`,
    label: config.plural,
    icon: BookIcon,
  };
});

const NAV_SECTIONS = [
  {
    label: null,
    links: [
      { to: '/dashboard', label: 'Dashboard', icon: DashboardIcon },
      { to: '/pos', label: 'Counter', icon: ScanIcon },
    ],
  },
  ...VOUCHER_NAV,
  {
    label: 'Accounting',
    links: [
      ...ACCOUNTING_NAV,
      { to: '/accounting', label: 'Chart of Accounts', icon: ChartOfAccountsIcon },
      { to: '/accounting/day-book', label: 'Day Book', icon: BookIcon },
      { to: '/accounting/trial-balance', label: 'Trial Balance', icon: BalanceIcon },
      { to: '/accounting/balance-sheet', label: 'Balance Sheet', icon: ReportIcon },
      { to: '/accounting/profit-loss', label: 'Profit & Loss', icon: ReportIcon },
      { to: '/accounting/cash-flow', label: 'Cash Flow', icon: CashFlowIcon },
      { to: '/accounting/outstanding', label: 'Outstanding', icon: OutstandingIcon },
    ],
  },
  {
    label: 'Records',
    links: [
      { to: '/parties', label: 'Parties', icon: PartyIcon },
      { to: '/items', label: 'Items', icon: ItemIcon },
      { to: '/locations', label: 'Locations', icon: WarehouseIcon },
      { to: '/stock-take', label: 'Stock Take', icon: StockTakeIcon },
      /* A wallet, not the credit card Outstanding uses. They were the same
         glyph, three rows apart in the same column, for two different
         things — money recorded as paid, and money still owed. */
      { to: '/payments', label: 'Payments', icon: PaymentIcon },
      { to: '/accounts', label: 'Cash & Bank', icon: BankIcon },
      { to: '/pricing', label: 'Pricing', icon: PriceListIcon },
      { to: '/manufacturing', label: 'Manufacturing', icon: ManufacturingIcon },
      { to: '/store', label: 'Online Store', icon: StoreIcon },
    ],
  },
  {
    label: 'Follow Up',
    links: [
      { to: '/recurring', label: 'Repeating', icon: RepeatIcon },
      { to: '/reminders', label: 'Reminders', icon: ReminderIcon },
      { to: '/messages', label: 'Messages', icon: SendIcon },
      { to: '/reports', label: 'Reports', icon: ReportIcon },
      { to: '/activity', label: 'Activity', icon: ActivityIcon },
      { to: '/data', label: 'Your Data', icon: DataIcon },
    ],
  },
];

/**
 * WHICH LINKS HAVE TO MATCH THE PATH EXACTLY.
 *
 * A `NavLink` without `end` is active whenever the current path STARTS with its
 * own, at a segment boundary. That is what you want almost everywhere — Sales
 * should stay lit while you are reading `/vouchers/sales/abc123`, because the
 * invoice is a Sales screen.
 *
 * It is wrong for a link that is the PARENT of another link. Chart of Accounts
 * is `/accounting`, and so is the front of `/accounting/day-book`,
 * `/accounting/trial-balance`, every register under `/accounting/vouchers/…`
 * and every ledger under `/accounting/ledgers/…`. So it was lit on every screen
 * in the module — permanently active, and on the Day Book two links claimed to
 * be the current one at the same time.
 *
 * DERIVED FROM THE TABLE, not listed. `end={link.to === '/dashboard'}` was a
 * hand-kept exception and it is the kind that goes stale: the moment somebody
 * adds `/reports/gst` beside `/reports`, the same fault reappears in a second
 * section and nobody thinks to come back here. Asking the table which of its
 * own paths is a prefix of another cannot fall behind the table.
 */
const NAV_PATHS = NAV_SECTIONS.flatMap((section) => section.links.map((link) => link.to));
const EXACT_MATCH_PATHS = new Set(
  NAV_PATHS.filter((path) => NAV_PATHS.some((other) => other.startsWith(`${path}/`)))
);

/* ------------------------------------------------------------------ *
 * Collapsing a section
 * ------------------------------------------------------------------ */

/**
 * Which sections the user has folded away, remembered per device.
 *
 * WHY IT IS PERSISTED. Sales alone is six documents and the whole sidebar is
 * twenty-eight links; a shop that never raises a quotation or a challan is
 * scrolling past them all day. Folding a section is only worth doing if it
 * stays folded — a collapse that resets on every navigation is a control that
 * costs a click and gives nothing back.
 *
 * Per device rather than per tenant, and for the same reason the theme is: it
 * is a fact about this screen and this person, not about the business.
 */
const NAV_COLLAPSE_KEY = 'trudesk-nav-collapsed';

/** Private mode can make localStorage throw on read as well as on write. */
function readCollapsed() {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(NAV_COLLAPSE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return new Set(Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeCollapsed(labels) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(NAV_COLLAPSE_KEY, JSON.stringify([...labels]));
  } catch {
    /* A preference that cannot be saved is not a reason to refuse the collapse
       — it just does not survive the reload. */
  }
}

/* ------------------------------------------------------------------ *
 * Shell
 * ------------------------------------------------------------------ */

/**
 * The shell is split in two so that the topbar and the routed screen below it
 * sit under ONE PageHeadingProvider: the screen publishes its title, the
 * topbar renders it. A provider inside <Shell /> could not do that — the
 * topbar would be a sibling of the state, not a consumer of it.
 */
export default function AppShell() {
  return (
    /**
     * The chart of accounts is loaded ONCE, here, and shared by every screen
     * beneath. Three listeners per screen would be a dozen on a dashboard, and
     * worse, each screen would build its own ledger index — the same tree walk,
     * repeated, arriving at answers that drift the first time one is fixed.
     *
     * Inside the shell rather than at the root, so an unauthenticated visitor
     * and the public shop window never open a listener on a tenant's ledgers.
     */
    <AccountingProvider>
      <PageHeadingProvider>
        <Shell />
      </PageHeadingProvider>
    </AccountingProvider>
  );
}

function Shell() {
  const { business, signOut, claims } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [theme, setTheme] = useTheme();
  const connection = useConnection();
  const { heading, setActionsSlot } = usePageHeadingValue() || {};

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  /**
   * Signing out ASKS FIRST.
   *
   * It is one click away from Settings in a column the user is in all day, it
   * is directly below a link they click often, and there is no undo: coming
   * back means the whole sign-in again, and on a shared counter machine it
   * means finding whoever knows the password. A half-typed invoice is lost
   * with it. The dialog costs one keystroke and removes the whole class of
   * accident.
   */
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      navigate('/sign-in', { replace: true });
    } catch (error) {
      toast.error('Could not sign out. Please try again.');
    } finally {
      /* Cleared even on success: the component can still be mounted for a
         frame before the redirect takes it away, and a dialog left spinning
         is what an interrupted sign-out looks like to the user. */
      setSigningOut(false);
      setSignOutOpen(false);
    }
  };

  /* --- collapsing a nav section ------------------------------------ */

  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggleSection = useCallback((label) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      writeCollapsed(next);
      return next;
    });
  }, []);

  /**
   * Whether a section holds the screen currently open.
   *
   * A collapsed section stays collapsed — being inside it does not force it
   * open, because a section that reopens itself the moment you fold it is not
   * a control. Instead its header carries a dot, so "where am I?" is still
   * answerable without expanding anything.
   *
   * The prefix test matches `NavLink`'s own rule, at a segment boundary, so
   * `/vouchers/sales/abc` counts as Sales and `/vouchers/sales-return` does
   * not count as `/vouchers/sales`.
   */
  const sectionHasActive = useCallback(
    (section) =>
      section.links.some(
        (link) =>
          location.pathname === link.to ||
          (!EXACT_MATCH_PATHS.has(link.to) && location.pathname.startsWith(`${link.to}/`))
      ),
    [location.pathname]
  );

  const businessName = business?.name || 'Your business';
  const gstin = business?.gstin || '';

  /**
   * The sidebar, filtered to what this role can actually open.
   *
   * A link to a door that will not open is worse than no link: it teaches the
   * user that the application is unreliable, and it does it every time they
   * glance at the sidebar. A section whose every link is gone disappears with
   * them rather than leaving an empty heading.
   */
  const navSections = useMemo(() => {
    const role = claims?.role || '';
    return NAV_SECTIONS.map((section) => ({
      ...section,
      links: section.links.filter((link) => canOpenRoute(role, link.to)),
    })).filter((section) => section.links.length > 0);
  }, [claims]);

  return (
    <div className="app-shell" data-drawer={drawerOpen ? 'open' : 'closed'}>
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>

      {drawerOpen && (
        <div
          className="drawer-scrim"
          role="presentation"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <aside className="app-sidebar" aria-label="Main navigation">
        {/* Which business this session is writing to, and the one detail that
            proves it. A user who keeps two firms in one account cannot tell
            them apart from the name alone — the names are usually similar and
            the GSTIN never is. */}
        <NavLink to="/settings" className="sidebar-brand" title="Open settings">
          <span className="sidebar-brand-mark" aria-hidden="true">
            {initials(businessName)}
          </span>
          <span className="sidebar-brand-body">
            <span className="sidebar-brand-text">{businessName}</span>
            {/* A GSTIN is 15 characters and the column is 240px wide, so it
                gets the whole second line. Truncated to "27AABCU9…" it
                identifies nothing, which is its only job here. */}
            <span className="sidebar-brand-meta">{gstin || 'GSTIN not added'}</span>
          </span>
        </NavLink>

        <nav className="sidebar-nav">
          {navSections.map((section) => {
            /* The first section has no heading, so there is nothing to click
               and nothing to fold — Dashboard and the Counter stay put. */
            const foldable = !!section.label;
            const isOpen = !foldable || !collapsed.has(section.label);
            const holdsActive = foldable && sectionHasActive(section);
            const listId = `nav-${(section.label || 'primary').toLowerCase().replace(/\W+/g, '-')}`;

            return (
              <div
                className="sidebar-section"
                key={section.label || 'primary'}
                data-collapsed={foldable && !isOpen ? 'true' : undefined}
              >
                {foldable && (
                  <button
                    type="button"
                    className="sidebar-section-label sidebar-section-toggle"
                    aria-expanded={isOpen}
                    aria-controls={listId}
                    onClick={() => toggleSection(section.label)}
                  >
                    <ChevronIcon className="sidebar-section-caret" size={12} aria-hidden="true" />
                    <span className="grow">{section.label}</span>
                    {/* Only when folded: expanded, the lit link says it. */}
                    {!isOpen && holdsActive && (
                      <span
                        className="sidebar-section-dot"
                        title="The screen you are on is in here"
                        aria-label="Contains the current screen"
                      />
                    )}
                  </button>
                )}
                {isOpen && (
                  <div id={listId}>
                    {section.links.map((link) => (
                      <NavLink
                        key={link.to}
                        to={link.to}
                        className="sidebar-link"
                        end={EXACT_MATCH_PATHS.has(link.to)}
                      >
                        <link.icon className="sidebar-link-icon" aria-hidden="true" />
                        {link.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          {/* The theme and connection controls used to be icon buttons in the
              topbar. They are chrome, not page actions, so they moved down
              here with the rest of the chrome and left the topbar to the
              screen's own buttons. Neither is only here: the theme also lives
              in Settings > Appearance. */}
          <div className="sidebar-utility">
            <button
              type="button"
              className="sidebar-utility-btn"
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            >
              {theme === 'dark' ? <LightThemeIcon size={16} /> : <DarkThemeIcon size={16} />}
            </button>

            <button
              type="button"
              className="sidebar-utility-btn"
              data-state={connection.isPaused ? 'paused' : undefined}
              aria-label={
                connection.isPaused ? 'Reconnect and sync' : 'Work offline until you reconnect'
              }
              title={
                connection.isPaused
                  ? 'Reconnect and send everything waiting'
                  : 'Stop waiting on the network. Everything is saved here and sent when you reconnect.'
              }
              onClick={() =>
                (connection.isPaused ? connection.resume() : connection.pause()).catch(() =>
                  toast.error('Could not change the connection state.')
                )
              }
            >
              {connection.isPaused ? <OnlineIcon size={16} /> : <OfflineIcon size={16} />}
            </button>
          </div>

          {/* A persistent indicator, not a toast, and not a button either.
              Being offline is a STATE that lasts until it stops, and a save
              made offline succeeds silently — the toast appears, the invoice
              is on screen, and the write is queued on this one device. It
              sits directly under the button that changes it, so the state and
              its switch are one thing to look at rather than two things at
              opposite corners of the screen. */}
          {(connection.isDisconnected || connection.isPaused) && (
            <span
              className="connection-pill"
              data-state={connection.isPaused ? 'paused' : 'offline'}
              role="status"
            >
              <OfflineIcon size={14} aria-hidden="true" />
              {connection.isPaused
                ? 'Working offline on purpose'
                : 'Offline — saved on this device'}
            </span>
          )}

          <NavLink to="/settings" className="sidebar-link">
            <SettingsIcon className="sidebar-link-icon" aria-hidden="true" />
            Settings
          </NavLink>

          <button
            type="button"
            className="sidebar-link sidebar-link-danger"
            onClick={() => setSignOutOpen(true)}
          >
            <SignOutIcon className="sidebar-link-icon" aria-hidden="true" />
            Sign out
          </button>
        </div>

        <ConfirmDialog
          open={signOutOpen}
          onCancel={() => setSignOutOpen(false)}
          onConfirm={handleSignOut}
          title="Sign out?"
          description="Anything you have not saved on this screen will be lost. You will need your password to get back in."
          confirmLabel="Sign out"
          cancelLabel="Stay signed in"
          pending={signingOut}
        />
      </aside>

      <div className="app-main">
        <header className="topbar">
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            icon={MenuIcon}
            className="mobile-only"
            aria-label="Open navigation"
            onClick={() => setDrawerOpen(true)}
          />

          {/* The screen's own name, on the same line as the buttons that act
              on it. It used to render in a second header band inside the
              content area, below an almost-empty topbar. */}
          {heading?.title && (
            <div className="topbar-heading">
              <h1 className="topbar-title" title={heading.title}>
                {heading.title}
              </h1>
              {heading.subtitle && (
                <p className="topbar-subtitle" title={heading.subtitle}>
                  {heading.subtitle}
                </p>
              )}
            </div>
          )}

          <div className="topbar-spacer" />

          {/* The screen's action buttons land HERE, portalled in by
              PageHeader. Empty on screens that declare none — hence the CSS
              :empty rule that collapses its gap. */}
          <div className="topbar-actions" ref={setActionsSlot} />
        </header>

        <main className="app-content" id="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
